import { APP_INITIALIZER, EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { CountryDomainService } from './country-domain.service';
import { ALL_COUNTRIES } from './country.model';
import { IdentityService } from '../auth/identity.service';

/**
 * Feeds the signed-in staff member's country assignment into {@link CountryDomainService}, which is
 * where every country-scoped URL in the SPA is resolved.
 *
 * A provider rather than an `inject(IdentityService)` inside `CountryDomainService`: the HTTP
 * interceptor already injects the country service, so giving that service an auth dependency invites
 * a DI cycle between the two.
 *
 * Subscribes without `take(1)` so re-login and token refresh both update the list — a staff member
 * whose assignment changed sees it after the refresh, not only after a reload. Signing out restores
 * every country, so the next session starts from the default rather than the previous user's scope.
 */
export function provideCountryAccess(): EnvironmentProviders {
    return makeEnvironmentProviders([
        {
            provide: APP_INITIALIZER,
            useFactory: (identityService: IdentityService, countryDomainService: CountryDomainService) => () => {
                identityService.stateChanges.subscribe({
                    next: state =>
                        countryDomainService.setAllowedCountries(state.user?.countries ?? [...ALL_COUNTRIES]),
                    // The stream is BehaviorSubject-backed and does not error in practice. If it ever
                    // did, the last known assignment stands — and the per-country AdminBff refuses
                    // anything outside it either way, so nothing here is load-bearing for access.
                    error: () => undefined,
                });
            },
            deps: [IdentityService, CountryDomainService],
            multi: true,
        },
    ]);
}
